/**
 * Game Utility Functions
 *
 * Collection of helper functions for Pokemon stats, colors, sprites, and grading.
 *
 * These utilities can work in two modes:
 * 1. With injected game data from GameDataContext (preferred)
 * 2. With direct imports for backwards compatibility during migration
 */

import React from 'react';
import { Heart, Shield, Sparkles, Swords, Wind } from 'lucide-react';
import {
  SUPPORT_CARDS as PROXY_SUPPORT_CARDS,
  GACHA_RARITY as PROXY_GACHA_RARITY,
  EVOLUTION_CHAINS as PROXY_EVOLUTION_CHAINS,
  MOVES as PROXY_MOVES,
  normalizeSupportName as proxyNormalizeSupportName,
  getSupportAtLimitBreak as proxyGetSupportAtLimitBreak
} from '../shared/gameData';
import { getPokemonSpriteScale } from '../constants/pokemonSpriteConfig';

// ============================================================================
// COLOR UTILITIES (no game data needed)
// ============================================================================

export const getTypeColor = (type) => {
  const colors = {
    Fire: '#dc2626',
    Water: '#2563eb',
    Grass: '#16a34a',
    Psychic: '#9333ea',
    Electric: '#ca8a04',
    Fighting: '#f97316',
    Dark: '#705848',
    Dragon: '#7038F8',
    Steel: '#B8B8D0',
    Colorless: '#6b7280',
    Normal: '#6b7280'
  };
  return colors[type] || '#6b7280';
};

export const getAptitudeColor = (grade) => {
  const colors = {
    F: '#000000',
    E: '#9ca3af',
    D: '#3b82f6',
    C: '#22c55e',
    B: '#ec4899',
    A: '#f97316',
    S: '#eab308'
  };
  return colors[grade] || '#6b7280';
};

export const getGradeColor = (grade) => {
  if (!grade) return '#6b7280'; // Default gray for undefined/null
  const baseGrade = grade.replace('+', '');
  const colors = {
    F: '#000000',
    E: '#9ca3af',
    D: '#3b82f6',
    C: '#22c55e',
    B: '#ec4899',
    A: '#f97316',
    S: '#eab308',
    UU: '#14b8a6'  // Teal
  };
  return colors[baseGrade] || '#6b7280';
};

export const getRarityColor = (rarity) => {
  const colors = {
    Common: '#9ca3af',
    Uncommon: '#22c55e',
    Rare: '#3b82f6',
    Legendary: '#eab308'
  };
  return colors[rarity] || '#6b7280';
};

// Grade rank values for sorting (higher = better)
const GRADE_RANK = { 'S': 6, 'A': 5, 'B': 4, 'C': 3, 'D': 2, 'E': 1, 'F': 0 };

/**
 * Sort aptitudes by grade (highest first)
 * @param {Object} aptitudes - Object with type/strategy keys and grade values
 * @returns {Array} - Array of [key, grade] pairs sorted by grade descending
 */
export const sortAptitudesByGrade = (aptitudes) => {
  if (!aptitudes) return [];
  return Object.entries(aptitudes).sort((a, b) => {
    const rankA = GRADE_RANK[a[1]] ?? -1;
    const rankB = GRADE_RANK[b[1]] ?? -1;
    return rankB - rankA;
  });
};

// ============================================================================
// LIMIT BREAK UTILITIES (no game data needed)
// ============================================================================

/**
 * Calculates the stat bonus multiplier from limit break level
 * Each limit break level adds 5% to base stats
 * @param limitBreakLevel - The limit break level (0-4)
 * @returns Multiplier (1.0, 1.05, 1.10, 1.15, 1.20)
 */
export const getLimitBreakMultiplier = (limitBreakLevel) => {
  return 1 + (Math.min(4, Math.max(0, limitBreakLevel || 0)) * 0.05);
};

/**
 * Applies limit break bonus to Pokemon base stats
 * @param baseStats - The base stats object {HP, Attack, Defense, Instinct, Speed}
 * @param limitBreakLevel - The limit break level (0-4)
 * @returns New stats object with limit break bonus applied (rounded)
 */
export const applyLimitBreakToStats = (baseStats, limitBreakLevel) => {
  if (!baseStats || !limitBreakLevel) return baseStats;

  const multiplier = getLimitBreakMultiplier(limitBreakLevel);
  const boostedStats = {};

  for (const [stat, value] of Object.entries(baseStats)) {
    boostedStats[stat] = Math.round(value * multiplier);
  }

  return boostedStats;
};

// ============================================================================
// GRADING UTILITIES (no game data needed)
// ============================================================================

/**
 * Calculates Pokemon grade based on total stats
 * Grade determines evolution thresholds and overall power level
 * @param stats - Pokemon stat object {HP, Attack, Defense, Instinct, Speed}
 * @returns Grade string (E, D, C, B, A, S, UU, etc.)
 */
export const getPokemonGrade = (stats) => {
  const totalStats = Object.values(stats).reduce((sum, val) => sum + val, 0);

  // Each grade is 200 stat points apart, starting from F at <200
  if (totalStats >= 3000) return 'UU+';
  if (totalStats >= 2800) return 'UU';
  if (totalStats >= 2600) return 'S+';
  if (totalStats >= 2400) return 'S';
  if (totalStats >= 2200) return 'A+';
  if (totalStats >= 2000) return 'A';
  if (totalStats >= 1800) return 'B+';
  if (totalStats >= 1600) return 'B';
  if (totalStats >= 1400) return 'C+';
  if (totalStats >= 1200) return 'C';
  if (totalStats >= 1000) return 'D+';
  if (totalStats >= 800) return 'D';
  if (totalStats >= 600) return 'E+';
  if (totalStats >= 400) return 'E';
  if (totalStats >= 200) return 'F+';
  return 'F';
};

/**
 * Returns the best strategy for a Pokemon based on strategyAptitudes
 * @param strategyAptitudes - Object with strategy grades {Scaler: 'A', Nuker: 'B', ...}
 * @returns Object with {name: string, grade: string} for the highest graded strategy
 */
export const getBestStrategy = (strategyAptitudes) => {
  if (!strategyAptitudes) return null;

  const gradeOrder = ['S', 'A', 'B', 'C', 'D', 'E', 'F'];
  let bestStrategy = null;
  let bestGradeIndex = gradeOrder.length;

  for (const [strategy, grade] of Object.entries(strategyAptitudes)) {
    const gradeIndex = gradeOrder.indexOf(grade);
    if (gradeIndex !== -1 && gradeIndex < bestGradeIndex) {
      bestGradeIndex = gradeIndex;
      bestStrategy = { name: strategy, grade };
    }
  }

  return bestStrategy;
};

// ============================================================================
// EVOLUTION UTILITIES (requires EVOLUTION_CHAINS)
// ============================================================================

/**
 * Returns the appropriate evolution form of a Pokemon based on its grade
 * Grade C = eligible for stage 1 evolution
 * Grade A = eligible for stage 2 evolution
 * @param pokemonName - The Pokemon name (can be any form in the evolution chain)
 * @param stats - Pokemon stat object to calculate grade, OR a grade string directly
 * @param EVOLUTION_CHAINS_PARAM - Evolution chains data (optional, uses proxy import if not provided)
 * @returns The appropriate evolution form name for display
 */
export const getEvolutionFormForGrade = (pokemonName, stats, EVOLUTION_CHAINS_PARAM) => {
  const EVOLUTION_CHAINS = EVOLUTION_CHAINS_PARAM || PROXY_EVOLUTION_CHAINS;
  if (!pokemonName || !EVOLUTION_CHAINS) return pokemonName;

  // Calculate grade from stats, or use directly if it's already a grade string
  let grade;
  if (typeof stats === 'string') {
    grade = stats;
  } else if (stats && typeof stats === 'object') {
    grade = getPokemonGrade(stats);
  } else {
    return pokemonName; // No stats provided, return original name
  }

  // Find the evolution chain this Pokemon belongs to
  let baseForm = null;
  let chainData = null;

  // Check if pokemonName is a base form
  if (EVOLUTION_CHAINS[pokemonName]) {
    baseForm = pokemonName;
    chainData = EVOLUTION_CHAINS[pokemonName];
  } else {
    // Search for this Pokemon in evolution chains (as stage1 or stage2)
    for (const [base, data] of Object.entries(EVOLUTION_CHAINS)) {
      if (data.stage1 === pokemonName || data.stage2 === pokemonName) {
        baseForm = base;
        chainData = data;
        break;
      }
    }
  }

  // If Pokemon doesn't evolve or isn't in evolution chains, return original name
  if (!chainData) {
    return pokemonName;
  }

  // Grade thresholds for evolution (matching EVOLUTION_CONFIG in gameData.js)
  // Stage 1 requires grade C or higher (C, C+, B, B+, A, A+, S, S+, UU, UU+)
  // Stage 2 requires grade A or higher (A, A+, S, S+, UU, UU+)

  // Full grade order with + variants for proper >= comparison
  const gradeOrder = ['F', 'F+', 'E', 'E+', 'D', 'D+', 'C', 'C+', 'B', 'B+', 'A', 'A+', 'S', 'S+', 'UU', 'UU+'];
  const gradeIndex = gradeOrder.indexOf(grade);
  const cIndex = gradeOrder.indexOf('C');  // Index 6
  const aIndex = gradeOrder.indexOf('A');  // Index 10

  // Determine which evolution form is appropriate
  if (chainData.stages === 2) {
    // 2-stage evolution (base -> stage1 -> stage2)
    if (gradeIndex >= aIndex) {
      // Grade A or higher: fully evolved (stage2)
      return chainData.stage2;
    } else if (gradeIndex >= cIndex) {
      // Grade C or higher (but below A): middle evolution (stage1)
      return chainData.stage1;
    } else {
      // Below grade C: base form
      return baseForm;
    }
  } else if (chainData.stages === 1) {
    // 1-stage evolution (base -> stage1)
    if (gradeIndex >= cIndex) {
      // Grade C or higher: evolved form
      return chainData.stage1;
    } else {
      // Below grade C: base form
      return baseForm;
    }
  }

  // Fallback
  return pokemonName;
};

/**
 * Returns the rarity of a Pokemon based on GACHA_RARITY
 * @param pokemonName - Name of the Pokemon
 * @param GACHA_RARITY_PARAM - Gacha rarity data (optional, uses proxy import if not provided)
 * @param EVOLUTION_CHAINS_PARAM - Evolution chains data (optional, uses proxy import if not provided)
 */
export const getPokemonRarity = (pokemonName, GACHA_RARITY_PARAM, EVOLUTION_CHAINS_PARAM) => {
  // Use proxy imports as fallback if parameters not provided
  const GACHA_RARITY = GACHA_RARITY_PARAM || PROXY_GACHA_RARITY;
  const EVOLUTION_CHAINS = EVOLUTION_CHAINS_PARAM || PROXY_EVOLUTION_CHAINS;

  // Check each rarity tier in GACHA_RARITY
  if (GACHA_RARITY) {
    if (GACHA_RARITY.Legendary?.pokemon?.includes(pokemonName)) return 'Legendary';
    if (GACHA_RARITY.Rare?.pokemon?.includes(pokemonName)) return 'Rare';
    if (GACHA_RARITY.Uncommon?.pokemon?.includes(pokemonName)) return 'Uncommon';
    if (GACHA_RARITY.Common?.pokemon?.includes(pokemonName)) return 'Common';
  }

  // Fallback: Check evolution chain for Pokemon not in gacha
  if (!EVOLUTION_CHAINS) return 'Common';

  const evolutionData = Object.entries(EVOLUTION_CHAINS).find(([base, data]) => {
    return base === pokemonName || data.stage1 === pokemonName || data.stage2 === pokemonName;
  });

  if (!evolutionData) return 'Common';

  const [, chainData] = evolutionData;

  // Fully evolved from 2-stage chain
  if (chainData.stages === 2 && chainData.stage2 === pokemonName) return 'Rare';
  // Middle-stage or fully evolved from 1-stage chain
  if (chainData.stage1 === pokemonName) return 'Uncommon';

  return 'Common';
};

/**
 * Gets the base species (first form) for any Pokemon in an evolution chain.
 * Used for trial restrictions where the entire evolution chain counts as "used".
 * @param pokemonName - Name of the Pokemon (any form in the chain)
 * @param EVOLUTION_CHAINS_PARAM - Evolution chains data (optional, uses proxy import if not provided)
 * @returns The base species name, or the original name if not found in any chain
 */
export const getBaseSpecies = (pokemonName, EVOLUTION_CHAINS_PARAM) => {
  const EVOLUTION_CHAINS = EVOLUTION_CHAINS_PARAM || PROXY_EVOLUTION_CHAINS;
  if (!pokemonName || !EVOLUTION_CHAINS) return pokemonName;

  // Check if pokemonName is itself a base form
  if (EVOLUTION_CHAINS[pokemonName]) {
    return pokemonName;
  }

  // Search for this Pokemon in evolution chains (as stage1 or stage2)
  for (const [base, data] of Object.entries(EVOLUTION_CHAINS)) {
    if (data.stage1 === pokemonName || data.stage2 === pokemonName) {
      return base;
    }
  }

  // Not found in any chain - return original name
  return pokemonName;
};

/**
 * Checks if two Pokemon are from the same evolution chain (same species family).
 * @param pokemon1 - First Pokemon name
 * @param pokemon2 - Second Pokemon name
 * @param EVOLUTION_CHAINS_PARAM - Evolution chains data (optional)
 * @returns true if they share the same base species
 */
export const isSameSpeciesFamily = (pokemon1, pokemon2, EVOLUTION_CHAINS_PARAM) => {
  return getBaseSpecies(pokemon1, EVOLUTION_CHAINS_PARAM) === getBaseSpecies(pokemon2, EVOLUTION_CHAINS_PARAM);
};

// ============================================================================
// INSPIRATION UTILITIES (server-authoritative - removed client calculations)
// ============================================================================
// NOTE: Inspiration generation and application are now handled entirely server-side
// - Inspirations generated in backend: routes/career/progression.js POST /complete
// - Inspirations applied in backend: routes/career/progression.js POST /apply-inspiration
// Frontend should use data from backend responses only

// REMOVED: generateInspirations() - backend generates based on B+ aptitudes
// REMOVED: checkAndApplyInspiration() - backend applies with cached RNG

// ============================================================================
// SPRITE COMPONENTS (no game data needed)
// ============================================================================

// Pokemon that require specific form names for PokeAPI
// Format: 'GameName': 'pokeapi-name'
export const POKEAPI_NAME_OVERRIDES = {
  // Size forms - default to average/normal
  'Pumpkaboo': 'pumpkaboo-average',
  'Gourgeist': 'gourgeist-average',
  // Gender-specific Pokemon - Nidoran uses special naming in PokeAPI
  'Nidoran♀': 'nidoran-f',
  'Nidoran♂': 'nidoran-m',
  // Gender forms - default to male
  'Meowstic': 'meowstic-male',
  'Indeedee': 'indeedee-male',
  // Battle forms - default to standard
  'Aegislash': 'aegislash-shield',
  'Mimikyu': 'mimikyu-disguised',
  'Morpeko': 'morpeko-full-belly',
  'Wishiwashi': 'wishiwashi-solo',
  'Minior': 'minior-red-meteor',
  'Eiscue': 'eiscue-ice',
  // Regional forms
  'Basculin': 'basculin-red-striped',
  // Misc forms
  'Lycanroc': 'lycanroc-midday',
  'Oricorio': 'oricorio-baile',
  'Wormadam': 'wormadam-plant',
  'Darmanitan': 'darmanitan-standard',
  'Toxtricity': 'toxtricity-amped',
  'Urshifu': 'urshifu-single-strike',
  'Zygarde': 'zygarde-50',
  'Shaymin': 'shaymin-land',
  'Giratina': 'giratina-altered',
  'Deoxys': 'deoxys-normal',
  'Hoopa': 'hoopa-confined',
  'Keldeo': 'keldeo-ordinary',
  'Meloetta': 'meloetta-aria',
  'Tornadus': 'tornadus-incarnate',
  'Thundurus': 'thundurus-incarnate',
  'Landorus': 'landorus-incarnate',
  'Enamorus': 'enamorus-incarnate',
  // Special name formatting
  'Ho-Oh': 'ho-oh',
  'Tapu Koko': 'tapu-koko',
  'Tapu Lele': 'tapu-lele',
  'Tapu Bulu': 'tapu-bulu',
  'Tapu Fini': 'tapu-fini',
  'Mr. Mime': 'mr-mime',
  'Mime Jr.': 'mime-jr',
  'Mr. Rime': 'mr-rime',
  'Jangmo-o': 'jangmo-o',
  'Hakamo-o': 'hakamo-o',
  'Kommo-o': 'kommo-o',
  'Type: Null': 'type-null',
  'Porygon-Z': 'porygon-z',
  'Wo-Chien': 'wo-chien',
  'Chien-Pao': 'chien-pao',
  'Ting-Lu': 'ting-lu',
  'Chi-Yu': 'chi-yu',
};

// Simple sprite cache - just stores URLs
const spriteCache = {};

export const PokemonSprite = ({ type, pokemonName, size = 64, useScale = true }) => {
  const [spriteUrl, setSpriteUrl] = React.useState(spriteCache[pokemonName] || null);

  React.useEffect(() => {
    // Guard against undefined or non-string pokemonName
    if (!pokemonName || typeof pokemonName !== 'string') {
      console.error('[PokemonSprite] pokemonName is invalid:', pokemonName);
      setSpriteUrl('error');
      return;
    }

    if (spriteCache[pokemonName]) {
      setSpriteUrl(spriteCache[pokemonName]);
      return;
    }

    // Use override name if this Pokemon has form variants in PokeAPI
    const apiName = POKEAPI_NAME_OVERRIDES[pokemonName] || pokemonName.toLowerCase();

    fetch(`https://pokeapi.co/api/v2/pokemon/${apiName}`)
      .then(res => res.json())
      .then(data => {
        // Use Black & White animated sprites, fallback to static front_default
        const bwAnimated = data.sprites?.versions?.['generation-v']?.['black-white']?.animated?.front_default;
        const url = bwAnimated || data.sprites.front_default;
        spriteCache[pokemonName] = url;
        setSpriteUrl(url);
      })
      .catch(err => {
        console.error('Failed to fetch Pokemon:', err);
        setSpriteUrl('error');
      });
  }, [pokemonName]);

  // Get per-Pokemon scale adjustment (only apply in battle contexts)
  const spriteScale = useScale ? getPokemonSpriteScale(pokemonName) : 1.0;
  const scaledSize = Math.round(size * spriteScale);

  if (!spriteUrl) {
    return <div style={{width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>Loading...</div>;
  }

  if (spriteUrl === 'error') {
    return <div style={{width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px'}}>{pokemonName}</div>;
  }

  return (
    <div style={{width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'}}>
      <img
        src={spriteUrl}
        alt={pokemonName}
        style={{
          width: scaledSize,
          height: scaledSize,
          objectFit: 'contain',
          imageRendering: 'pixelated'
        }}
      />
    </div>
  );
};

// Backwards compatibility wrapper
// useScale: true = apply per-Pokemon scaling (for battles), false = uniform size (for UI cards)
export const generatePokemonSprite = (type, pokemonName, size = 64, useScale = true) => {
  return <PokemonSprite type={type} pokemonName={pokemonName} size={size} useScale={useScale} />;
};

export const generateTrainerSprite = (gymNum) => {
  return (
    <svg width="60" height="60" viewBox="0 0 60 60">
      <circle cx="30" cy="20" r="12" fill="#ffcc99" stroke="#000" strokeWidth="2"/>
      <rect x="18" y="32" width="24" height="20" fill="#4488ff" stroke="#000" strokeWidth="2"/>
      <text x="30" y="25" fontSize="10" textAnchor="middle" fill="#000">G{gymNum}</text>
    </svg>
  );
};

export const StatIcon = ({ stat, size = 16 }) => {
  const icons = {
    HP: <Heart size={size} className="text-red-500" />,
    Attack: <Swords size={size} className="text-orange-500" />,
    Defense: <Shield size={size} className="text-blue-500" />,
    Instinct: <Sparkles size={size} className="text-purple-500" />,
    Speed: <Wind size={size} className="text-green-500" />
  };
  return icons[stat] || null;
};

// ============================================================================
// SUPPORT CARD UTILITIES (requires SUPPORT_CARDS, normalizeSupportName, getSupportAtLimitBreak)
// ============================================================================

/**
 * Returns the dominant move type from a support card's moveHints
 * @param moveHints - Array of move names
 * @param MOVES - The MOVES object containing move definitions
 * @returns The most common move type, or null if no moves
 * Note: In case of ties, prefers non-Normal types over Normal
 */
export const getDominantMoveType = (moveHints, MOVES_PARAM) => {
  const MOVES = MOVES_PARAM || PROXY_MOVES;
  if (!moveHints || moveHints.length === 0 || !MOVES) return null;

  // Count occurrences of each type
  const typeCounts = {};
  for (const move of moveHints) {
    const moveData = MOVES[move];
    if (moveData?.type) {
      typeCounts[moveData.type] = (typeCounts[moveData.type] || 0) + 1;
    }
  }

  // Find the type with highest count, preferring non-Normal types in ties
  let dominantType = null;
  let maxCount = 0;
  for (const [type, count] of Object.entries(typeCounts)) {
    if (count > maxCount) {
      maxCount = count;
      dominantType = type;
    } else if (count === maxCount && dominantType === 'Normal' && type !== 'Normal') {
      // In case of tie, prefer non-Normal type over Normal
      dominantType = type;
    }
  }

  return dominantType;
};

/**
 * Get support card attributes with limit break applied
 * @param supportKey - Support card key
 * @param SUPPORT_CARDS_PARAM - Support cards data (optional, uses proxy import if not provided)
 * @param limitBreakLevelOrNormalize - Limit break level (0-4) OR normalize function for backwards compat
 */
export const getSupportCardAttributes = (supportKey, SUPPORT_CARDS_PARAM, limitBreakLevelOrNormalize) => {
  // Handle various call signatures for backwards compatibility:
  // New: getSupportCardAttributes(key, SUPPORT_CARDS, limitBreak)
  // Very old: getSupportCardAttributes(key, SUPPORT_CARDS)

  const SUPPORT_CARDS = SUPPORT_CARDS_PARAM || PROXY_SUPPORT_CARDS;
  const normalizeFn = proxyNormalizeSupportName;
  const getLBFn = proxyGetSupportAtLimitBreak;

  // Determine limit break level from third parameter if it's a number
  let limitBreakLevel = 4;
  if (typeof limitBreakLevelOrNormalize === 'number') {
    limitBreakLevel = limitBreakLevelOrNormalize;
  }

  if (!supportKey) return null;

  // Normalize legacy support names to new format
  const normalizedKey = normalizeFn(supportKey);
  const card = SUPPORT_CARDS[normalizedKey];
  if (!card) return null;

  // Get limit break adjusted card
  const lbCard = getLBFn ? getLBFn(normalizedKey, limitBreakLevel) : null;
  const effectiveCard = lbCard || card;

  // New structure uses:
  // - baseStats: { HP, Attack, Defense, Instinct, Speed }
  // - trainingBonus: { typeMatch, otherStats, maxFriendshipTypeMatch }
  // - appearanceRate (0.25-0.65)
  // - typeMatchPreference (0.50-0.90)
  // - specialEffect: { statGainMultiplier, failRateReduction, maxEnergyBonus, restBonus, etc. } or null
  // - moveHints: []
  // - description

  // Return complete attributes with backwards-compatible field names
  return {
    ...effectiveCard,
    // Base stat bonuses (direct stat additions when support is equipped)
    baseStatIncrease: effectiveCard.baseStats || { HP: 0, Attack: 0, Defense: 0, Instinct: 0, Speed: 0 },
    // Training bonuses
    typeBonusTraining: effectiveCard.trainingBonus?.typeMatch || 4,
    generalBonusTraining: effectiveCard.trainingBonus?.otherStats || 2,
    friendshipBonusTraining: effectiveCard.trainingBonus?.maxFriendshipTypeMatch || 8,
    // Friendship
    initialFriendship: effectiveCard.initialFriendship ?? 30,
    // Appearance
    appearanceChance: effectiveCard.appearanceRate || 0.40,
    typeAppearancePriority: effectiveCard.typeMatchPreference || 0.65,
    // Move hints
    moveHints: effectiveCard.moveHints || ['BodySlam', 'HyperBeam'],
    // Effect - convert new specialEffect to old effect format for compatibility
    effect: effectiveCard.specialEffect ? {
      type: effectiveCard.specialEffect.statGainMultiplier ? 'training_boost' :
            effectiveCard.specialEffect.maxEnergyBonus ? 'energy_boost' :
            effectiveCard.specialEffect.skillPointMultiplier ? 'experience_boost' : 'special',
      description: effectiveCard.description || '',
      ...effectiveCard.specialEffect
    } : { type: 'none', description: effectiveCard.description || '' },
    // Pokemon type (Fire, Water, etc.) - use explicit type field, don't overwrite with supportType
    type: effectiveCard.type || null
  };
};

// ============================================================================
// BATTLE UTILITIES (no game data needed)
// ============================================================================

export const getBattleDisplayName = (combatant) => {
  return combatant.name || combatant.pokemon || 'Unknown';
};
